import useModule from "hooks/useModule";
import { useRef } from "react";
import { Button } from "reactstrap";
import { Popover, Whisper } from "rsuite";
import AddColumnOptions from "./AddColumnOptions";
import _ from "underscore";
import { useAtomValue } from "jotai";
import { IsMobileAtom } from "../../../App";

/**
 * ColumnAdd - Add button with AddColumnOptions popover.
 *
 * @param {ReactTable} table - The ReactTable table object.
 * @param {Array<string>} hiddenFields - Fields (field.APIName) to filter from the availableFields we generate.
 * @param {string} moduleAPIName - The current module the table is using
 * @param {function} onColumnAdd - Callback function for when a field (column) is added
 * @param {Array<string>} fields - An array of fieldAPINames (used on mobileScreen)
 */
const ColumnAdd = ({
  table,
  hiddenFields = [],
  moduleAPIName,
  onColumnAdd,
  description,
  multiSubitemFieldAPIName,
  fields = null,
}) => {
  const mobileScreen = useAtomValue(IsMobileAtom);
  const { module, getModule } = useModule(moduleAPIName);
  const fieldAPINames = mobileScreen
    ? fields
    : table.getAllColumns().map((c) => c.id);

  let availableFields = [];

  if (module?.Fields?.length) {
    if (multiSubitemFieldAPIName) {
      const multiSubitemField = _.findWhere(module?.Fields, {
        APIName: multiSubitemFieldAPIName,
      });
      availableFields = multiSubitemField?.SubitemFields.filter(
        (f) =>
          !fieldAPINames?.includes(f.APIName) &&
          !hiddenFields.includes(f.APIName)
      );
    } else {
      availableFields = module?.Fields?.filter(
        (f) =>
          !fieldAPINames?.includes(f.APIName) &&
          !hiddenFields.includes(f.APIName)
      );
    }
  }

  const getSubitemFields = () => {
    let subitemFields = [];

    if (multiSubitemFieldAPIName) return subitemFields;

    if (module?.Fields?.length) {
      for (const field of module.Fields) {
        if (field.FieldType === "Subitem") {
          const availableSubitemFields = field.SubitemFields.filter(
            (f) => !fieldAPINames?.includes(`${field.APIName}.${f.APIName}`)
          );
          const subitemFieldObject = {
            Name: field.Name,
            Fields: availableSubitemFields,
          };
          subitemFields.push(subitemFieldObject);
        }
      }
    }

    return subitemFields;
  };

  const getSingleReferenceFields = () => {
    let singleReferenceFields = [];

    if (multiSubitemFieldAPIName) return subitemFields;

    if (module?.Fields?.length) {
      for (const field of module.Fields) {
        if (field.FieldType === "SingleReference") {
          let referenceSourceList = field.SourcesList;

          if (
            (typeof referenceSourceList !== "string" &&
              referenceSourceList?.length > 1) ||
            referenceSourceList === "Any"
          ) {
            continue;
          }

          referenceSourceList = Array.isArray(referenceSourceList)
            ? referenceSourceList[0]
            : referenceSourceList;

          const referenceModuleName = referenceSourceList.split(".")[2];
          const referenceModule = getModule(referenceModuleName);
          const availableSingleReferenceFields = referenceModule?.Fields.filter(
            (f) => !fieldAPINames?.includes(`${field.APIName}.${f.APIName}`)
          );

          const singleReferenceObject = {
            APIName: field.APIName,
            Name: field.Name,
            Fields: availableSingleReferenceFields,
          };
          singleReferenceFields.push(singleReferenceObject);
        }
      }
    }
    return singleReferenceFields;
  };

  const subitemFields = getSubitemFields();
  const singleReferenceFields = getSingleReferenceFields();

  const dropdownRef = useRef();

  return (
    <Whisper
      ref={dropdownRef}
      placement={mobileScreen ? "bottom" : "autoVerticalEnd"}
      trigger="click"
      speaker={
        <Popover
          className="sidebar-popover"
          style={{ width: mobileScreen ? "326px" : "400px" }}
        >
          <AddColumnOptions
            availableFields={availableFields}
            subitemFields={subitemFields}
            singleReferenceFields={singleReferenceFields}
            moduleAPIName={moduleAPIName}
            onColumnAdd={onColumnAdd}
            description={description}
          />
        </Popover>
      }
    >
      {mobileScreen ? (
        <i className="mdi mdi-plus-circle" style={{ fontSize: 24 }}></i>
      ) : (
        <Button
          size="sm"
          color="secondary"
          outline
          className="btn-icon-only"
          style={{ height: "1.5rem", width: "1.5rem" }}
        >
          <i className="mdi mdi-plus-circle" style={{ fontSize: 16 }}></i>
        </Button>
      )}
    </Whisper>
  );
};

export default ColumnAdd;
